import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { archiveEntries, groupOptions, typeOptions } from "@/mocks/archive";

const typeColor: Record<string, string> = {
  anime: "bg-primary-100 text-primary-800",
  manga: "bg-accent-100 text-accent-900",
  game: "bg-secondary-100 text-secondary-800",
  novel: "bg-primary-100 text-primary-800",
  voice: "bg-accent-100 text-accent-900",
  fanwork: "bg-secondary-100 text-secondary-800",
};

export default function ArchivePage() {
  const [group, setGroup] = useState("全部");
  const [type, setType] = useState("全部");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    return archiveEntries.filter((e) => {
      const matchGroup = group === "全部" || e.groupBy === group;
      const matchType = type === "全部" || e.type === type;
      const q = query.trim().toLowerCase();
      const matchQ =
        !q ||
        e.title.toLowerCase().includes(q) ||
        e.desc.toLowerCase().includes(q);
      return matchGroup && matchType && matchQ;
    });
  }, [group, type, query]);

  return (
    <Layout>
      <section className="bg-background-950 py-12 md:py-16">
        <div className="container-site">
          <nav className="flex flex-wrap items-center gap-1 text-xs text-background-300" aria-label="面包屑">
            <Link to="/" className="hover:text-background-50">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-100">归档</span>
          </nav>
          <h1 className="mt-4 font-heading text-2xl font-bold text-background-50 md:text-3xl">
            归档索引
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-background-200 md:text-base">
            归档列表把资源按系列、作者、作品等维度归类，整理贴持续补全。想批量收藏，直接查归档列表即可，省去零散收藏的麻烦。
          </p>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="container-site py-10 md:py-14">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <p className="mb-2 text-xs font-medium text-foreground-500">按分组筛选</p>
              <div className="flex flex-wrap gap-2">
                {groupOptions.map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setGroup(g)}
                    className={`whitespace-nowrap rounded-full px-1 py-1 text-sm font-medium transition-colors ${
                      group === g
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    <span className="px-2">{g}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-medium text-foreground-500">按类型筛选</p>
              <div className="flex flex-wrap gap-2">
                {typeOptions.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    className={`whitespace-nowrap rounded-full px-1 py-1 text-sm font-medium transition-colors ${
                      type === t
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    <span className="px-2">{t}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="relative mt-6 w-full md:w-80">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索归档标题或描述"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>

          <p className="mt-6 text-sm text-foreground-500">
            共 <span className="font-medium text-primary-700">{filtered.length}</span> 条归档记录
          </p>

          {filtered.length > 0 ? (
            <div className="mt-6 overflow-hidden rounded-lg border border-background-200/70 bg-background-50">
              <div className="hidden grid-cols-12 gap-4 border-b border-background-200/70 bg-background-100/70 px-5 py-3 text-xs font-medium text-foreground-500 md:grid">
                <div className="col-span-5">归档条目</div>
                <div className="col-span-2">类型</div>
                <div className="col-span-2">分组</div>
                <div className="col-span-3">更新日期</div>
              </div>
              <div className="flex flex-col divide-y divide-background-200/70">
                {filtered.map((entry) => (
                  <div
                    key={entry.id}
                    className="grid grid-cols-1 gap-2 px-5 py-4 transition-colors hover:bg-background-100/50 md:grid-cols-12 md:items-center md:gap-4"
                  >
                    <div className="md:col-span-5">
                      <h3 className="text-sm font-medium text-foreground-800 md:text-base">
                        {entry.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-500">{entry.desc}</p>
                    </div>
                    <div className="md:col-span-2">
                      <span
                        className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ${typeColor[entry.typeKey] ?? "bg-background-200 text-foreground-700"}`}
                      >
                        {entry.type}
                      </span>
                    </div>
                    <div className="md:col-span-2">
                      <span className="text-xs text-secondary-700">{entry.groupBy}</span>
                    </div>
                    <div className="flex items-center gap-2 md:col-span-3 md:justify-between">
                      <span className="text-xs text-foreground-500">{entry.date}</span>
                      <Link
                        to="/collections"
                        className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-primary-700 hover:text-primary-800"
                      >
                        查看合集
                        <i className="ri-arrow-right-line"></i>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100/60 py-16 text-center">
              <i className="ri-archive-drawer-line text-4xl text-foreground-400"></i>
              <p className="mt-4 text-sm text-foreground-600">
                没有找到匹配的归档记录，试试调整筛选条件。
              </p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}